import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Brain, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import GlassCard from "@/components/ui-fleet/GlassCard";

const SUGGESTIONS = [
  "Which machines need service this week?",
  "Show idle fuel waste by site",
  "Top 5 highest utilization machines",
  "Predict next 3 failures",
  "Which dealer has highest repeat rate?",
  "Flag all SLA breaches over 30 days",
];

const STORAGE_KEY = "opsora_ai_chat_history";
const MAX_MESSAGES = 50;

const WELCOME = {
  id: "welcome",
  role: "ai",
  text: "Hi — I'm Opsora AI. I have real-time visibility into all 100 machines, service calls, sales, and telemetry. Ask me anything about your fleet.",
  timestamp: new Date().toISOString(),
};

const loadHistory = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return { messages: [WELCOME], hasOldSession: false };
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed) || parsed.length === 0) return { messages: [WELCOME], hasOldSession: false };
    return { messages: parsed, hasOldSession: true };
  } catch {
    return { messages: [WELCOME], hasOldSession: false };
  }
};

function MarkdownLite({ text }) {
  const lines = text.split("\n");
  return (
    <div className="space-y-1.5">
      {lines.map((ln, i) => {
        if (ln.startsWith("| ")) {
          const cells = ln.split("|").slice(1, -1).map((c) => c.trim());
          return (
            <div key={i} className="grid gap-2 text-[12px] py-1 border-b font-mono" style={{
              gridTemplateColumns: `repeat(${cells.length}, 1fr)`,
              borderColor: "var(--glass-border-btm)",
              color: "var(--slate-700)",
            }}>
              {cells.map((c, j) => <span key={j}>{c}</span>)}
            </div>
          );
        }
        const parts = ln.split(/(\*\*.*?\*\*)/g);
        return (
          <p key={i} className="text-[13px]" style={{ color: "var(--slate-700)" }}>
            {parts.map((p, j) =>
              p.startsWith("**") && p.endsWith("**")
                ? <strong key={j} style={{ color: "var(--slate-900)" }}>{p.slice(2, -2)}</strong>
                : <span key={j}>{p}</span>
            )}
          </p>
        );
      })}
    </div>
  );
}

export default function AIAssistant() {
  const [sessionId] = useState(() => `fleet-${Date.now()}`);
  const initial = loadHistory();
  const [msgs, setMsgs] = useState(initial.messages);
  const [sessionDividerAt] = useState(initial.hasOldSession ? initial.messages.length : -1);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, loading]);

  // Persist on change (cap at MAX_MESSAGES)
  useEffect(() => {
    try {
      const trimmed = msgs.length > MAX_MESSAGES ? msgs.slice(-MAX_MESSAGES) : msgs;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    } catch {}
  }, [msgs]);

  const send = async (text) => {
    const q = text || input.trim();
    if (!q || loading) return;
    setInput("");
    const userMsg = {
      id: `u-${Date.now()}`,
      role: "user",
      text: q,
      timestamp: new Date().toISOString(),
    };
    setMsgs((m) => [...m, userMsg]);
    setLoading(true);
    try {
      const r = await api.post("/ai/chat", { session_id: sessionId, message: q });
      const aiMsg = {
        id: `a-${Date.now()}`,
        role: "ai",
        text: r.data.response,
        timestamp: r.data.timestamp || new Date().toISOString(),
      };
      setMsgs((m) => [...m, aiMsg]);
    } catch (e) {
      setMsgs((m) => [...m, {
        id: `a-${Date.now()}`,
        role: "ai",
        text: "Sorry — I hit a snag connecting. Please try again.",
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = () => {
    if (!confirmClear) {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 2000);
      return;
    }
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setMsgs([{ ...WELCOME, timestamp: new Date().toISOString() }]);
    setConfirmClear(false);
  };

  const sessionStartLabel = new Date().toLocaleString("en-IN", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });

  return (
    <div data-testid="ai-assistant">
      <PageHeader title="Ask Opsora" breadcrumb="AI Assistant"
        right={
          <>
            <span className="status-pill copper"><Sparkles size={11} /> Claude Sonnet 4.5</span>
            <button
              onClick={clearHistory}
              className="btn-ghost flex items-center gap-1.5"
              style={{ fontSize: "11px", color: confirmClear ? "var(--status-red)" : "var(--slate-500)" }}
              data-testid="ai-clear-history"
            >
              <Trash2 size={12} />
              {confirmClear ? "Confirm Clear?" : "Clear History"}
            </button>
          </>
        }
      />

      <div className="grid grid-cols-12 gap-5">
        <GlassCard className="col-span-8 p-0 flex flex-col" style={{ minHeight: 620 }}>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="chat-history">
            {msgs.map((m, i) => (
              <React.Fragment key={m.id || i}>
                {i === sessionDividerAt && (
                  <div className="flex items-center gap-3 my-4" data-testid="session-divider">
                    <div className="flex-1 h-px" style={{ background: "var(--glass-border-btm)" }} />
                    <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--slate-300)" }}>
                      New session · {sessionStartLabel}
                    </span>
                    <div className="flex-1 h-px" style={{ background: "var(--glass-border-btm)" }} />
                  </div>
                )}
                <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "ai" && (
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center mr-2 flex-shrink-0"
                      style={{ background: "var(--accent-dim)", color: "var(--accent)" }}
                    >
                      <Brain size={13} />
                    </div>
                  )}
                  <div className={`max-w-[78%] ${m.role === "user" ? "" : "glass-card-sm p-3"}`}
                    style={{
                      background: m.role === "user" ? "linear-gradient(135deg, var(--accent-light) 0%, var(--accent) 100%)" : undefined,
                      color: m.role === "user" ? "white" : undefined,
                      padding: m.role === "user" ? "10px 16px" : undefined,
                      borderRadius: m.role === "user" ? "16px 16px 4px 16px" : undefined,
                      boxShadow: m.role === "user" ? "0 6px 18px rgba(196, 98, 45, 0.35)" : undefined,
                    }}
                  >
                    {m.role === "user" ? (
                      <span className="text-[13px]">{m.text}</span>
                    ) : (
                      <MarkdownLite text={m.text} />
                    )}
                  </div>
                </div>
              </React.Fragment>
            ))}
            {loading && (
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ background: "var(--accent-dim)", color: "var(--accent)" }}>
                  <Brain size={13} />
                </div>
                <div className="glass-card-sm px-3 py-2.5 flex items-center gap-1.5">
                  <Dot delay={0} />
                  <Dot delay={150} />
                  <Dot delay={300} />
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t" style={{ borderColor: "var(--glass-border-btm)" }}>
            <div className="glass-card-sm flex items-center gap-2 p-2">
              <input
                type="text"
                placeholder="Ask anything about your fleet…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                className="flex-1 px-3 py-2 bg-transparent outline-none text-[14px]"
                style={{ color: "var(--slate-900)" }}
                data-testid="ai-input"
              />
              <button onClick={() => send()} disabled={loading || !input.trim()}
                className="btn-copper flex items-center gap-1.5" data-testid="ai-send">
                <Send size={13} /> Send
              </button>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="col-span-4 p-5 self-start">
          <div className="text-[11px] uppercase tracking-widest mb-3" style={{ color: "var(--slate-500)" }}>
            Currently analyzing
          </div>
          <div className="space-y-2 mb-5">
            <ContextChip label="100 machines" detail="across 5 sites" />
            <ContextChip label="4 critical alerts" detail="fuel & temp" />
            <ContextChip label="Live telemetry" detail="updated 1m ago" />
          </div>
          <div className="text-[11px] uppercase tracking-widest mb-3" style={{ color: "var(--slate-500)" }}>
            Quick queries
          </div>
          <div className="space-y-2">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                onClick={() => send(s)}
                className="glass-card-sm w-full text-left text-[12px] p-3 hover:bg-white/70 transition-colors"
                style={{ color: "var(--slate-700)" }}
                data-testid={`suggestion-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        </GlassCard>
      </div>
    </div>
  );
}

function ContextChip({ label, detail }) {
  return (
    <div className="glass-card-sm p-2.5 flex items-center justify-between">
      <span className="text-[12px] font-medium" style={{ color: "var(--slate-900)" }}>{label}</span>
      <span className="text-[10px] font-mono" style={{ color: "var(--slate-500)" }}>{detail}</span>
    </div>
  );
}
function Dot({ delay }) {
  return (
    <span
      className="w-1.5 h-1.5 rounded-full"
      style={{
        background: "var(--accent)",
        animation: `pulseAccent 1.4s infinite ${delay}ms ease-in-out`
      }}
    />
  );
}
